import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import ProductTour from "@/components/ProductTour";
import { toast } from "sonner";

const TourContext = createContext(null);

export const TOUR_STEPS = [
  {
    id: "sidebar",
    route: "/",
    target: '[data-testid="sidebar"]',
    chapter: "The shape of Mahano OS",
    title: "Everything lives in one rail",
    body: "Home is your cockpit; below it sit People, Companies, Projects, Finance, Calendar, Tasks and more. You can rename or hide any module later.",
  },
  {
    id: "start-day",
    route: "/",
    target: '[data-testid="start-my-day-btn"]',
    chapter: "Your morning",
    title: "Start My Day",
    body: "One press and Mahano reads your projects, tasks, money and industry news, then writes you a calm morning brief.",
  },
  {
    id: "brief",
    route: "/",
    target: '[data-testid="ai-brief-card"]',
    chapter: "Your morning",
    title: "The AI brief and suggestions",
    body: "The brief lands here with next-step suggestions and an industry pulse pulled from a live web search.",
  },
  {
    id: "reschedule",
    route: "/",
    target: '[data-testid="reschedule-day-btn"]',
    chapter: "Planning",
    title: "Reschedule Day",
    body: "This packs every open task into real time slots inside your working hours — today and the next working day — around your meetings and pinned blocks.",
  },
  {
    id: "schedule",
    route: "/",
    target: '[data-testid="schedule-card"]',
    chapter: "Planning",
    title: "Your day, in blocks",
    body: "Meetings and tasks merge into one timeline with real start and end times. Tomorrow's Plan does the same for the next day.",
  },
  {
    id: "customise",
    route: "/",
    target: '[data-testid="customise-layout-btn"]',
    chapter: "Make it yours",
    title: "Customise the dashboard",
    body: "Drag widgets by the handle to reorder them, hide what you don't need, and reset whenever you like. Your layout is remembered.",
  },
  {
    id: "money",
    route: "/",
    target: '[data-testid="money-watch-card"]',
    chapter: "Money",
    title: "Money Watch",
    body: "Outstanding, overdue and collected-this-month at a glance, so cash never surprises you.",
  },
  {
    id: "templates",
    route: "/projects",
    target: '[data-testid="new-project-from-template-btn"]',
    chapter: "Projects",
    title: "Start from a template",
    body: "Pick a blueprint and Mahano builds the stages, the task list and the folder structure for you in one click.",
  },
  {
    id: "project-page",
    route: "/projects",
    target: '[data-testid="projects-page"]',
    chapter: "Projects",
    title: "Kanban, Timeline and folders",
    body: "Open a project to drag tasks across stages, flip to the Timeline for stage overlaps and deadlines, and drop files straight into the right folder.",
  },
  {
    id: "tasks",
    route: "/tasks",
    target: '[data-testid="tasks-page"]',
    chapter: "Tasks",
    title: "Click a task, get the whole story",
    body: "The task drawer holds description, subtasks, comments, attachments, an assignee and a quiet log of every change.",
  },
  {
    id: "finance",
    route: "/finance",
    target: '[data-testid="finance-tabs"]',
    chapter: "Money",
    title: "Invoices, expenses, payments",
    body: "Build an invoice with line items and tax, export a branded PDF in three layouts, then record payments against it.",
  },
  {
    id: "news",
    route: "/news",
    target: '[data-testid="news-page"]',
    chapter: "Intelligence",
    title: "Industry news, filtered for you",
    body: "Mahano searches the live web for your topics and scores each headline for relevance — no doom-scrolling required.",
  },
  {
    id: "settings",
    route: "/settings",
    target: '[data-testid="tab-workspace"]',
    chapter: "Make it yours",
    title: "White-label and working hours",
    body: "Rename modules, set your brand colours and logo, and tell Mahano your working hours. You can restart this tour here any time.",
  },
  {
    id: "done",
    route: "/",
    target: '[data-testid="getting-started-card"]',
    chapter: "You're set",
    title: "Five things to try",
    body: "This checklist ticks itself off as you go. Finish it and Mahano is fully wired to your business.",
  },
];

export function TourProvider({ children }) {
  const { settings, update } = useSettings() || {};
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const autoRan = useRef(false);

  // Auto-run once for a freshly onboarded user landing on Home
  useEffect(() => {
    if (autoRan.current || !settings) return;
    if (settings.onboarded === true && !settings.tour_completed && location.pathname === "/") {
      autoRan.current = true;
      setOpen(true);
    }
  }, [settings, location.pathname]);

  const startTour = useCallback(() => { autoRan.current = true; setOpen(true); }, []);

  const close = useCallback(async (completed) => {
    setOpen(false);
    try { await update?.({ tour_completed: true }); }
    catch (err) { console.error("Could not save tour state", err); }
    if (completed) toast.success("That's the tour — you can replay it from Settings any time");
  }, [update]);

  return (
    <TourContext.Provider value={{ startTour, tourOpen: open }}>
      {children}
      <ProductTour
        steps={TOUR_STEPS}
        open={open}
        onFinish={() => close(true)}
        onSkip={() => close(false)}
      />
    </TourContext.Provider>
  );
}

export const useTour = () => useContext(TourContext) || { startTour: () => {} };
